import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  InputAdornment,
  IconButton,
  Drawer,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';

const claimOptions = [
  { code: 'E01', label: 'Educational Assistance (1st to 7th & 8th to 10th)', amount: 2500 },
  { code: 'E02', label: 'Educational Assistance (10th or 12th 50%+)', amount: 10000 },
  { code: 'E03', label: 'Educational Assistance (11th and 12th Std)', amount: 10000 },
  { code: 'E04', label: 'Educational Assistance (Degree Course)', amount: 20000 },
  { code: 'E05', label: 'Educational Assistance (Medical Degree)', amount: 100000 },
  { code: 'E06', label: 'Educational Assistance (Diploma Course)', amount: 20000 },
  { code: 'E07', label: 'Reimbursement of MS-CIT Course Fees', amount: 0 },
  { code: 'S1', label: 'Financial Assistance for First Marriage', amount: 30000 },
  { code: 'S3', label: 'Distribution of Personality Development Books', amount: 0 },
  { code: 'S4', label: 'Financial Assistance for Purchase of Tools', amount: 5000 },
  { code: 'S5', label: 'Pradhan Mantri Jeevan Jyoti Bima Yojana', amount: 0 },
  { code: 'S6', label: 'Pradhan Mantri Suraksha Bima Yojana', amount: 0 },
  { code: 'S7', label: 'Skill Development Scheme', amount: 0 },
  { code: 'H1', label: 'Financial Assistance for Delivery (Natural/Caesarean)', amount: 15000 },
  { code: 'H2', label: 'Medical Assistance for Serious Ailments', amount: 100000 },
  { code: 'H3', label: 'Fixed Deposit for Female Child (Family Planning)', amount: 100000 },
  { code: 'H4', label: 'Financial Assistance for Permanent Disability', amount: 200000 },
  { code: 'H5', label: 'Mahatma Jyotiba Phule Jan Arogya Yojana', amount: 0 },
  { code: 'F01', label: 'Financial Assistance for Accidental Death', amount: 500000 },
  { code: 'F02', label: 'Financial Assistance for Natural Death', amount: 200000 },
  { code: 'F03', label: 'Loan Subsidy for House Construction', amount: 0 },
  { code: 'F05', label: 'Financial Assistance for Funeral', amount: 10000 },
  { code: 'F06', label: 'Financial Assistance for Widow/Widower', amount: 24000 },
];

const emptyForm = {
  id: null,
  userid: null,
  name: '',
  gender: 'Male',
  mobile: '',
  whatsapp_no: '',
  aadhar: '',
  epic_no: '',
  card_no: '',
  vidhansabha: 'Karad South',
  village: '',
  booth_no: '',
  gan_name: '',
  zp_name: '',
  reference: '',
  taluka: '',
  district: '',
  form_date: '',
  ack_no: '',
  claim_code: '',
  claim_amount: '',
  wfc_date: '',
  wfc_location: 'Karad',
  wfc_other_location: '',
  wfc_remarks: '',
  operator: '',
  form_status: 'UNRECEIVED',
  approval_date: '',
  payment_status: 'UNPAID',
  cash_amount: '',
  online_amount: '',
  // Education fields
  student_name: '',
  student_saral_id: '',
  attendance_75: '',
  bonafide_year: '',
  approval_status: 'PENDING',
  payment_date: '',
  form_owner: '',
};

// API base - set `REACT_APP_API_BASE` in .env if needed
const API_BASE = process.env.REACT_APP_API_BASE;

export default function Claim({ userid, user }) {
  const isAdmin = user?.user_role === 'ADMIN';
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    name: '',
    mobile: '',
    card_no: '',
    aadhar: '',
    claim_code: '',
    form_owner: '',
    form_date_from: '',
    form_date_to: '',
    wfc_date_from: '',
    wfc_date_to: '',
    form_status: '',
    payment_status: '',
  });

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/claim-records`);
      const data = await res.json();
      setRecords(data.map(mapFromApi));
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  function mapFromApi(a) {
    const toInputDate = (d) => (d ? d.slice(0, 10) : '');
    return {
      ...a,
      id: a.id,
      name: a.name || '',
      gender: a.gender || 'Male',
      mobile: a.mobile || '',
      whatsapp_no: a.whatsapp_no || '',
      aadhar: a.aadhar || '',
      epic_no: a.epic_no || '',
      card_no: a.card_no || '',
      vidhansabha: a.vidhansabha || 'Karad South',
      village: a.village || '',
      booth_no: a.booth_no || '',
      gan_name: a.gan_name || '',
      zp_name: a.zp_name || '',
      reference: a.reference || '',
      taluka: a.taluka || '',
      district: a.district || '',
      form_date: toInputDate(a.form_date),
      ack_no: a.ack_no || '',
      claim_code: a.claim_code || '',
      claim_amount: a.claim_amount || '',
      wfc_date: toInputDate(a.wfc_date),
      wfc_location: a.wfc_location || 'Karad',
      wfc_other_location: a.wfc_other_location || '',
      wfc_remarks: a.wfc_remarks || '',
      operator: a.operator || '',
      approval_date: toInputDate(a.approval_date),
      payment_status: a.payment_status || 'UNPAID',
      cash_amount: a.cash_amount || '',
      online_amount: a.online_amount || '',
      student_name: a.student_name || '',
      student_saral_id: a.student_saral_id || '',
      attendance_75: a.attendance_75 || '',
      bonafide_year: a.bonafide_year || '',
      approval_status: a.approval_status ? a.approval_status.toUpperCase() : 'PENDING',
      payment_date: toInputDate(a.payment_date),
      form_owner: a.form_owner || '',
      form_status: (a.form_status === 'RECEIVED' ? 'Received' :
        (a.form_status === 'UNRECEIVED' ? 'Un-Received' : (a.form_status || 'Submitted'))),
    };
  }

  function mapToApi(f, uid) {
    const data = { ...f, userid: uid };
    // Convert empty strings to null for database compatibility
    ['form_date', 'wfc_date', 'approval_date', 'payment_date', 'claim_amount', 'cash_amount', 'online_amount'].forEach(key => {
      if (data[key] === '') data[key] = null;
    });
    return data;
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((s) => {
      let next = { ...s, [name]: value };

      // Clear approval_date if status is no longer ACCEPTED
      if (name === 'approval_status' && value !== 'ACCEPTED') {
        next.approval_date = '';
      }

      // Clear education fields if claim_code no longer starts with 'E'
      if (name === 'claim_code' && !value.startsWith('E')) {
        next.student_name = '';
        next.student_saral_id = '';
        next.attendance_75 = '';
        next.bonafide_year = '';
      }

      // Clear other location if location is not 'Other'
      if (name === 'wfc_location' && value !== 'Other') {
        next.wfc_other_location = '';
      }

      return next;
    });
  }

  function handleClaimChange(e) {
    const code = e.target.value;
    const opt = claimOptions.find((c) => c.code === code);
    setForm((s) => ({
      ...s,
      claim_code: code,
      claim_amount: opt ? opt.amount : ''
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (saving) return;
    setSaving(true);

    const method = form.id ? 'PUT' : 'POST';
    const url = form.id ? `${API_BASE}/claim-records/${form.id}` : `${API_BASE}/claim-records`;

    try {
      const payload = mapToApi(form, userid);
      if (method === 'PUT') payload._user_role = user?.user_role || '';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        fetchRecords();
        resetForm();
        setDrawerOpen(false);
      } else {
        alert('Failed to save record');
      }
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  }

  function handleEdit(id) {
    const rec = records.find((r) => r.id === id);
    if (!rec) return;
    if (!isAdmin && rec.is_locked && !rec.unlock_granted_by) {
      alert('This record is locked. Please ask an admin to unlock it first.');
      return;
    }
    setForm({ ...rec });
    setEditing(true);
    setDrawerOpen(true);
  }

  async function handleToggleLock(id) {
    try {
      const res = await fetch(`${API_BASE}/claim-records/${id}/toggle-lock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ admin_name: user?.namename || 'admin' })
      });
      if (!res.ok) throw new Error('Toggle lock failed');
      await fetchRecords();
    } catch (err) {
      alert('Error toggling lock: ' + err.message);
    }
  }

  async function handleDelete(id) {
    const rec = records.find((r) => r.id === id);
    if (!rec) return;
    if (!isAdmin && rec.is_locked && !rec.unlock_granted_by) {
      alert('This record is locked. Please ask an admin to unlock it first.');
      return;
    }

    if (!window.confirm('Delete this record?')) return;
    try {
      const res = await fetch(`${API_BASE}/claim-records/${id}`, { method: 'DELETE' });
      if (res.ok) {
        fetchRecords();
      } else {
        alert('Failed to delete');
      }
    } catch (err) {
      console.error('Delete error:', err);
    }
  }

  function handleFilterChange(e) {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const filteredRecords = React.useMemo(() => {
    const m = (val = '') => (val == null ? '' : String(val).toLowerCase());
    return records.filter((r) => {
      if (!m(r.name).includes(m(filters.name))) return false;
      if (!m(r.mobile).includes(m(filters.mobile))) return false;
      if (!m(r.aadhar).includes(m(filters.aadhar))) return false;
      if (!m(r.claim_code).includes(m(filters.claim_code))) return false;
      if (!m(r.card_no).includes(m(filters.card_no))) return false;
      if (!m(r.form_owner).includes(m(filters.form_owner))) return false;
      if (filters.form_date_from && r.form_date < filters.form_date_from) return false;
      if (filters.form_date_to && r.form_date > filters.form_date_to) return false;
      if (filters.wfc_date_from && r.wfc_date < filters.wfc_date_from) return false;
      if (filters.wfc_date_to && r.wfc_date > filters.wfc_date_to) return false;
      if (filters.form_status && r.form_status !== filters.form_status) return false;
      if (filters.payment_status && r.payment_status !== filters.payment_status) return false;
      return true;
    });
  }, [records, filters]);

  function resetForm() {
    setForm(emptyForm);
    setEditing(false);
    setDrawerOpen(false);
  }

  return (
    <Box sx={{ p: 1 }}>
      <Paper sx={{ p: 2, bgcolor: 'white' }} elevation={4}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          <Typography variant="h6" sx={{ color: '#0b4a6f', flexGrow: 1, fontWeight: 'bold' }}>Claim Form Records</Typography>
          <Chip label={`${records.length} records`} color="primary" variant="outlined" size="small" />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => { resetForm(); setDrawerOpen(true); }}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
          >
            Add New Claim
          </Button>
        </Box>

        <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)', border: '1px solid #e0e0e0', borderRadius: 1 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {['Worker Name', 'Mobile', 'MH No', 'Aadhar', 'Form owner', 'Claim Code', 'Amount', 'Form Date', 'WFC Date', 'Pay Status', 'Actions'].map((h) => (
                  <TableCell key={h} sx={{ bgcolor: '#0b4a6f', color: 'white', fontWeight: 'bold', py: 1.5 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="name" value={filters.name} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="primary" /></InputAdornment>, disableUnderline: true }} placeholder="Search..." /></TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="mobile" value={filters.mobile} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="card_no" value={filters.card_no} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="aadhar" value={filters.aadhar} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="form_owner" value={filters.form_owner} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="claim_code" value={filters.claim_code} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }} />
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <TextField size="small" variant="standard" name="form_date_from" type="date" value={filters.form_date_from} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black', width: '100%' }} InputProps={{ disableUnderline: true }} InputLabelProps={{ shrink: true }} label="From" />
                    <TextField size="small" variant="standard" name="form_date_to" type="date" value={filters.form_date_to} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black', width: '100%' }} InputProps={{ disableUnderline: true }} InputLabelProps={{ shrink: true }} label="To" />
                  </Box>
                </TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <TextField size="small" variant="standard" name="wfc_date_from" type="date" value={filters.wfc_date_from} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black', width: '100%' }} InputProps={{ disableUnderline: true }} InputLabelProps={{ shrink: true }} label="From" />
                    <TextField size="small" variant="standard" name="wfc_date_to" type="date" value={filters.wfc_date_to} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black', width: '100%' }} InputProps={{ disableUnderline: true }} InputLabelProps={{ shrink: true }} label="To" />
                  </Box>
                </TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}>
                  <Select size="small" variant="standard" name="payment_status" value={filters.payment_status || ''} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black', minWidth: 80 }}>
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="PAID">PAID</MenuItem>
                    <MenuItem value="UNPAID">UNPAID</MenuItem>
                  </Select>
                </TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRecords.map((r) => (
                <TableRow key={r.id} hover sx={{ '&:nth-of-type(even)': { bgcolor: '#fafafa' } }}>
                  <TableCell sx={{ fontWeight: 500 }}>{r.name}</TableCell>
                  <TableCell>{r.mobile}</TableCell>
                  <TableCell>{r.card_no}</TableCell>
                  <TableCell>{r.aadhar}</TableCell>
                  <TableCell>{r.form_owner}</TableCell>
                  <TableCell>{r.claim_code}</TableCell>
                  <TableCell>{r.claim_amount}</TableCell>
                  <TableCell>{formatDate(r.form_date)}</TableCell>
                  <TableCell>{formatDate(r.wfc_date)}</TableCell>
                  <TableCell>
                    <Chip
                      label={r.payment_status}
                      size="small"
                      color={r.payment_status === 'PAID' ? 'success' : 'error'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      {isAdmin && (
                        <IconButton size="small" onClick={() => handleToggleLock(r.id)}
                          title={r.unlock_granted_by ? `Unlock granted — click to re-lock` : 'Click to unlock for editing'}
                          sx={{ bgcolor: r.unlock_granted_by ? '#fff3e0' : '#fce4ec', '&:hover': { bgcolor: r.unlock_granted_by ? '#ffe0b2' : '#f8bbd0' } }}>
                          {r.unlock_granted_by ? <LockOpenIcon fontSize="small" color="warning" /> : <LockIcon fontSize="small" color="error" />}
                        </IconButton>
                      )}
                      <IconButton size="small" color="primary" onClick={() => handleEdit(r.id)}
                        disabled={!isAdmin && r.is_locked && !r.unlock_granted_by}
                        title={!isAdmin && r.is_locked && !r.unlock_granted_by ? 'Record is locked' : 'Edit'}
                        sx={{ bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' }, opacity: (!isAdmin && r.is_locked && !r.unlock_granted_by) ? 0.4 : 1 }}>
                        {(!isAdmin && r.is_locked && !r.unlock_granted_by) ? <LockIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(r.id)} 
                        disabled={!isAdmin && r.is_locked && !r.unlock_granted_by}
                        title={!isAdmin && r.is_locked && !r.unlock_granted_by ? 'Record is locked' : 'Delete'}
                        sx={{ bgcolor: '#ffebee', '&:hover': { bgcolor: '#ffcdd2' }, opacity: (!isAdmin && r.is_locked && !r.unlock_granted_by) ? 0.4 : 1 }}>
                        {(!isAdmin && r.is_locked && !r.unlock_granted_by) ? <LockIcon fontSize="small" /> : <DeleteIcon fontSize="small" />}
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box component="form" onSubmit={handleSubmit} sx={{ width: { xs: '100vw', sm: 500 }, p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: '#0b4a6f', flexGrow: 1, fontWeight: 'bold' }}>
              {editing ? 'Edit Claim' : 'Add New Claim'}
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', flexGrow: 1, pr: 1 }}>
            <TextField label="Worker Name" name="name" value={form.name} onChange={handleChange} required fullWidth variant="outlined" />

            <FormControl fullWidth>
              <InputLabel id="gender-label">Gender</InputLabel>
              <Select labelId="gender-label" label="Gender" name="gender" value={form.gender} onChange={handleChange}>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
              </Select>
            </FormControl>

            <TextField label="Mobile No" name="mobile" value={form.mobile} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="Whatsapp Mobile Number" name="whatsapp_no" value={form.whatsapp_no} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="Aadhar No" name="aadhar" value={form.aadhar} onChange={handleChange} fullWidth variant="outlined" />
            {/* <TextField label="EPIC No" name="epic_no" value={form.epic_no} onChange={handleChange} fullWidth variant="outlined" /> */}
            <TextField label="Village" name="village" value={form.village} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="Taluka" name="taluka" value={form.taluka} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="District" name="district" value={form.district} onChange={handleChange} fullWidth variant="outlined" />

            <FormControl fullWidth variant="outlined">
              <InputLabel id="form-owner-label">Form owner</InputLabel>
              <Select labelId="form-owner-label" label="Form owner" name="form_owner" value={form.form_owner} onChange={handleChange}>
                <MenuItem value="NITIN SIR">NITIN SIR</MenuItem>
                <MenuItem value="KUMBHAR MADAM">KUMBHAR MADAM</MenuItem>
                <MenuItem value="KAMBALE MADAM">KAMBALE MADAM</MenuItem>
                <MenuItem value="TAMBAVE OFFICE">TAMBAVE OFFICE</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Reference" name="reference" value={form.reference} onChange={handleChange} fullWidth variant="outlined" />


            <TextField label="MH No. (Card No.)" name="card_no" value={form.card_no} onChange={handleChange} fullWidth variant="outlined" />

            {/* <FormControl fullWidth>
              <InputLabel id="vidh-label">Vidhansabha</InputLabel>
              <Select labelId="vidh-label" label="Vidhansabha" name="vidhansabha" value={form.vidhansabha} onChange={handleChange}>
                <MenuItem value="Karad South">Karad South</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl> */}



            <TextField label="Online Form Date" name="form_date" type="date" InputLabelProps={{ shrink: true }} value={form.form_date} onChange={handleChange} fullWidth variant="outlined" />

            <TextField label="WFC Visit Date" name="wfc_date" type="date" InputLabelProps={{ shrink: true }} value={form.wfc_date} onChange={handleChange} fullWidth variant="outlined" />

            <FormControl fullWidth>
              <InputLabel id="wfc-loc-label">WFC Visit Location</InputLabel>
              <Select labelId="wfc-loc-label" label="WFC Visit Location" name="wfc_location" value={form.wfc_location} onChange={handleChange}>
                <MenuItem value="Karad">Karad</MenuItem>
                <MenuItem value="North Karad">North Karad</MenuItem>
                <MenuItem value="Patan">Patan</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>

            {form.wfc_location === 'Other' && (
              <TextField label="Other WFC Center (Manually)" name="wfc_other_location" value={form.wfc_other_location} onChange={handleChange} fullWidth variant="outlined" />
            )}

            <TextField label="WFC Visit Remarks" name="wfc_remarks" value={form.wfc_remarks} onChange={handleChange} multiline rows={3} fullWidth variant="outlined" />

            <TextField label="Acknowledgement No." name="ack_no" value={form.ack_no} onChange={handleChange} fullWidth variant="outlined" />


            <FormControl fullWidth>
              <InputLabel id="claim-code-label">Claim Code</InputLabel>
              <Select labelId="claim-code-label" label="Claim Code" name="claim_code" value={form.claim_code} onChange={handleClaimChange}>
                <MenuItem value="">-- Select --</MenuItem>
                {claimOptions.map((c) => (
                  <MenuItem key={c.code} value={c.code}>{`${c.code} — ${c.label}`}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {form.claim_code && form.claim_code.startsWith('E') && (
              <Box sx={{ border: '1px solid #1976d2', p: 2, borderRadius: 1, display: 'flex', flexDirection: 'column', gap: 2, bgcolor: '#e3f2fd' }}>
                <Typography variant="subtitle2" sx={{ color: '#1976d2', fontWeight: 'bold' }}>Education Claim Details</Typography>
                <TextField label="Student Name" name="student_name" value={form.student_name} onChange={handleChange} fullWidth variant="outlined" size="small" />
                <TextField label="Student Saral ID" name="student_saral_id" value={form.student_saral_id} onChange={handleChange} fullWidth variant="outlined" size="small" />
                <TextField label="75% Attendance for Ed. Year" name="attendance_75" value={form.attendance_75} onChange={handleChange} fullWidth variant="outlined" size="small" />
                <TextField label="Bonafide Year" name="bonafide_year" value={form.bonafide_year} onChange={handleChange} fullWidth variant="outlined" size="small" />
              </Box>
            )}

            <TextField label="Claim Amount" name="claim_amount" value={form.claim_amount} onChange={handleChange} type="number" fullWidth variant="outlined" helperText="You can put amount manually if needed" />


            <TextField label="Operator Name" name="operator" value={form.operator} onChange={handleChange} fullWidth variant="outlined" />

            <FormControl fullWidth>
              <InputLabel id="form-status-label">Form Status</InputLabel>
              <Select labelId="form-status-label" label="Form Status" name="form_status" value={form.form_status} onChange={handleChange}>
                <MenuItem value="Submitted">Submitted</MenuItem>
                <MenuItem value="Accepted">Accepted</MenuItem>
                <MenuItem value="Rejected">Rejected</MenuItem>
                <MenuItem value="Re-Submitted">Re-Submitted</MenuItem>
                <MenuItem value="Received">Received</MenuItem>
                <MenuItem value="Un-Received">Un-Received</MenuItem>
              </Select>
            </FormControl>

            {/* <FormControl fullWidth>
              <InputLabel id="appr-status-label">Approval Status</InputLabel>
              <Select labelId="appr-status-label" label="Approval Status" name="approval_status" value={form.approval_status} onChange={handleChange}>
                <MenuItem value="ACCEPTED">Accepted</MenuItem>
                <MenuItem value="REJECTED">Rejected</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
              </Select>
            </FormControl> */}

            {form.approval_status === 'ACCEPTED' && (
              <TextField label="Approval Date" name="approval_date" type="date" InputLabelProps={{ shrink: true }} value={form.approval_date} onChange={handleChange} fullWidth variant="outlined" />
            )}

            <Box sx={{ border: '1px solid #ddd', p: 2, borderRadius: 1, bgcolor: '#f9f9f9' }}>
              <Typography variant="subtitle2" sx={{ mb: 2, color: '#0b4a6f', fontWeight: 'bold' }}>Payment Details</Typography>
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="payment-status-label">Payment Status</InputLabel>
                  <Select labelId="payment-status-label" label="Payment Status" name="payment_status" value={form.payment_status} onChange={handleChange}>
                    <MenuItem value="PAID">PAID</MenuItem>
                    <MenuItem value="UNPAID">UNPAID</MenuItem>
                  </Select>
                </FormControl>

                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Cash Amount Rs."
                    name="cash_amount"
                    type="number"
                    value={form.cash_amount}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                  <TextField
                    label="Online Amount Rs."
                    name="online_amount"
                    type="number"
                    value={form.online_amount}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                </Stack>
                <TextField label="Payment Date" name="payment_date" type="date" InputLabelProps={{ shrink: true }} value={form.payment_date} onChange={handleChange} fullWidth variant="outlined" />
              </Stack>
            </Box>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={2}>
            <Button type="submit" variant="contained" color="primary" fullWidth size="large" sx={{ borderRadius: 2 }} disabled={saving}>
              {saving ? 'Saving...' : (editing ? 'Update' : 'Save')}
            </Button>
            <Button variant="outlined" onClick={resetForm} fullWidth size="large" sx={{ borderRadius: 2 }}>
              Reset
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </Box>
  );
}
